import { iamApiSlice } from './iamApiSlice';

export interface Organization {
  id: string;
  name: string;
  code: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version?: number;
  creatorName?: string;
}

export type OrgResponse = Organization[];

export interface OrgPage {
  totalCount: number;
  records: Organization[];
}

export interface OrgListParams {
  pageNo?: number;
  pageSize?: number;
  name?: string;
  status?: string;
  code?: string;
}

export const orgApi = iamApiSlice.injectEndpoints({
  endpoints: builder => ({
    getOrgs: builder.query<OrgResponse, void>({
      query: () => '/orgs/my',
      providesTags: ['Organization'],
    }),
    getAllOrgs: builder.query<OrgResponse, void>({
      query: () => '/orgs/all',
      providesTags: ['Organization'],
    }),
    getOrgsPage: builder.query<OrgPage, OrgListParams>({
      query: params => ({ url: '/orgs', params }),
      providesTags: ['Organization'],
    }),
    createOrg: builder.mutation<Organization, { name: string }>({
      query: body => ({
        url: '/orgs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organization'],
    }),
    verifyInvitation: builder.query<
      { orgName: string; email: string; status: string; userExists: boolean },
      string
    >({
      query: token => `/orgs/invitations/verify?token=${token}`,
    }),
    acceptInvitation: builder.mutation<void, string>({
      query: token => ({
        url: `/orgs/invitations/accept?token=${token}`,
        method: 'POST',
      }),
      invalidatesTags: ['Organization'],
    }),
    rejectInvitation: builder.mutation<void, string>({
      query: token => ({
        url: `/orgs/invitations/reject?token=${token}`,
        method: 'POST',
      }),
    }),
    inviteUser: builder.mutation<void, { orgId: string; email: string }>({
      query: ({ orgId, email }) => ({
        url: `/orgs/${orgId}/invite`,
        method: 'POST',
        body: { email },
      }),
    }),
    updateOrg: builder.mutation<Organization, { id: string; name: string }>({
      query: ({ id, ...body }) => ({
        url: `/orgs/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Organization'],
    }),
    syncOrgDefaults: builder.mutation<void, string>({
      query: orgId => ({
        url: `/orgs/${orgId}/sync-defaults`,
        method: 'POST',
      }),
    }),
    removeOrgMember: builder.mutation<void, string>({
      query: userId => ({
        url: `/orgs/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization', 'User'],
    }),
  }),
});

export const {
  useGetOrgsQuery,
  useGetAllOrgsQuery,
  useGetOrgsPageQuery,
  useCreateOrgMutation,
  useVerifyInvitationQuery,
  useAcceptInvitationMutation,
  useRejectInvitationMutation,
  useInviteUserMutation,
  useUpdateOrgMutation,
  useSyncOrgDefaultsMutation,
  useRemoveOrgMemberMutation,
} = orgApi;
